import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useSettingsStore } from "@/data/settingsStore";

export const getExportWatermarkEnabled = () => {
  return useSettingsStore.getState().getSetting('kaynin_export_watermark_enabled', true) as boolean;
};

export const setExportWatermarkEnabled = (enabled: boolean) => {
  useSettingsStore.getState().updateSetting('kaynin_export_watermark_enabled', enabled);
};

export const generateExport = (
  data: any[],
  filename: string,
  title: string,
  format: 'excel' | 'word' | 'pdf'
) => {
  if (!data?.length) return;

  const isWatermark = getExportWatermarkEnabled();
  const headers = Object.keys(data[0] || {});
  const rows = data.map(item => Object.values(item));

  if (format === 'excel') {
    // Watermark pour fichier CSV : Un commentaire en première ligne
    const watermarkPrefix = isWatermark ? '"# [ KAYN\'IN ]"\n' : '';
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + watermarkPrefix + headers.join(",") + "\n" + rows.map(r => r.map(c => `"${c}"`).join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } else if (format === 'pdf') {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(15);
    doc.text(title, 14, 15);

    autoTable(doc, { 
      head: [headers], 
      body: rows as string[][], 
      startY: 23,
      didDrawPage: function (data) {
        if (isWatermark) {
          // Ajout du filigrane diagonal KAYN'IN
          doc.setTextColor(230, 230, 230); // Gris très clair pour simuler la transparence
          doc.setFontSize(80);
          doc.text("KAYN'IN", data.settings.margin.left + 50, doc.internal.pageSize.getHeight() / 2 + 20, {
            angle: 45,
            align: "center"
          });
          // Reset de la couleur pour le reste du doc
          doc.setTextColor(0, 0, 0); 
        }
      }
    });
    doc.save(`${filename}.pdf`);

  } else if (format === 'word') {
    // Watermark visuel SVG intégré pour Ms Word en HTML web format
    const watermarkCSS = isWatermark 
      ? `body {
           position: relative;
         }
         body::before {
           content: "KAYN\\'IN";
           position: fixed;
           top: 30%;
           left: 20%;
           font-size: 150px;
           color: #f1f5f9; /* Gris très clair */
           transform: rotate(-45deg);
           z-index: -1;
           white-space: nowrap;
           font-family: sans-serif;
           font-weight: bold;
         }` 
      : ``;

    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>${filename}</title>
        <style>
          ${watermarkCSS}
          table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
          th { padding: 10px; text-align: left; background: #e5e7eb; border: 1px solid #999; }
          td { padding: 8px; border: 1px solid #ccc; }
          h2 { text-align: center; font-family: Arial, sans-serif; }
        </style>
      </head>
      <body>
        <h2>${title}</h2>
        <table>
          <tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>
          ${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("")}
        </table>
      </body>
    </html>`;

    const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.doc`;
    link.click();
  }
};

export const exportDebateAnalysisReport = (
  activeDebate: any,
  filteredContributions: any[],
  existingReport: any,
  format: 'excel' | 'word' | 'pdf',
  includeStats: boolean,
  includeWatermark: boolean,
  chartsImage?: string
) => {
  if (!activeDebate) return;
  const title = activeDebate.title;
  const d = new Date(activeDebate.createdAt);
  const dateStr = `${d.toLocaleDateString("fr-FR", { month: "long" })} ${d.getFullYear()}`;
  
  const validsForStats = filteredContributions.filter(c => c.status === "valide");
  const realStats = {
     total: validsForStats.length,
     hommes: validsForStats.filter(c => c.genre === "homme").length,
     femmes: validsForStats.filter(c => c.genre === "femme").length,
     ong: validsForStats.filter(c => c.authorCategory === "ONG").length,
     citoyens: validsForStats.filter(c => c.authorCategory === "CITOYEN").length,
     partis: validsForStats.filter(c => c.authorCategory === "PARTI").length,
     institutions: validsForStats.filter(c => c.authorCategory === "INSTITUTION").length,
  };
  
  if (format === 'pdf') {
    const doc = new jsPDF();
    
    if (includeWatermark) {
      doc.setTextColor(240, 240, 240);
      doc.setFontSize(80);
      doc.text("KAYN'IN", 105, 150, { angle: 45, align: "center" });
      doc.setTextColor(0, 0, 0);
    }
    
    doc.setFontSize(16);
    doc.text("Rapport de Débat", 14, 20);
    doc.setFontSize(12);
    doc.text(`Sujet : ${title}`, 14, 30);
    doc.text(`Date de publication : ${dateStr}`, 14, 38);
    
    let currentY = 50;

    if (activeDebate.adminConclusion) {
       doc.setFontSize(14);
       doc.text("Conclusion de l'Administration", 14, currentY);
       currentY += 8;
       doc.setFontSize(10);
       const lines = doc.splitTextToSize(activeDebate.adminConclusion, 180);
       doc.text(lines, 14, currentY);
       currentY += lines.length * 5 + 10;
    }

    doc.setFontSize(14);
    doc.text("Registre des participations", 14, currentY);
    currentY += 8;
    
    const tableData = filteredContributions.map(c => [
      c.authorName,
      c.authorActivity || 'Non renseigné',
      c.preferredLanguage === 'ar' ? 'Arabe' : (c.preferredLanguage === 'zgh' ? 'Tamazight' : 'Français'),
      new Date(c.createdAt).toLocaleDateString("fr-FR") + " " + new Date(c.createdAt).toLocaleTimeString("fr-FR", { hour: '2-digit', minute: '2-digit' }),
      c.text,
      `${c.likes} L / ${c.dislikes || 0} D`
    ]);
    
    autoTable(doc, {
      startY: currentY,
      head: [["Participant", "Activité", "Langue", "Date & Heure", "Avis", "Interactions"]],
      body: tableData,
      styles: { fontSize: 8 },
      columnStyles: { 4: { cellWidth: 70 } }
    });
    
    currentY = (doc as any).lastAutoTable.finalY + 15;
    
    if (includeStats) {
       doc.addPage();
       if (includeWatermark) {
         doc.setTextColor(240, 240, 240);
         doc.setFontSize(80);
         doc.text("KAYN'IN", 105, 150, { angle: 45, align: "center" });
         doc.setTextColor(0, 0, 0);
       }
       doc.setFontSize(14);
       doc.text("Statistiques Analytiques du Débat", 14, 20);
       doc.setFontSize(10);
       
       doc.text(`Total des participations validées : ${realStats.total}`, 14, 35);
       doc.text(`Hommes : ${realStats.hommes} | Femmes : ${realStats.femmes}`, 14, 45);
       
       doc.text(`Répartition par Acteurs :`, 14, 60);
       doc.text(`- Citoyens : ${realStats.citoyens}`, 20, 68);
       doc.text(`- ONG : ${realStats.ong}`, 20, 76);
       doc.text(`- Partis Politiques : ${realStats.partis}`, 20, 84);
       doc.text(`- Institutions : ${realStats.institutions}`, 20, 92);

       if (chartsImage) {
           doc.addImage(chartsImage, 'PNG', 14, 100, 180, 100);
       }
    }
    
    doc.save(`Rapport_Debat_${activeDebate.id}.pdf`);
    
  } else if (format === 'word' || format === 'excel') {
    const headers = ["Participant", "Activité", "Langue", "Date & Heure", "Avis", "Likes", "Dislikes"];
    const rows = filteredContributions.map(c => [
         c.authorName,
         c.authorActivity || 'Non renseigné',
         c.preferredLanguage === 'ar' ? 'Arabe' : (c.preferredLanguage === 'zgh' ? 'Tamazight' : 'Français'),
         new Date(c.createdAt).toLocaleDateString("fr-FR") + " " + new Date(c.createdAt).toLocaleTimeString("fr-FR", { hour: '2-digit', minute: '2-digit' }),
         c.text,
         c.likes.toString(),
         (c.dislikes || 0).toString()
    ]);
    
    if (format === 'excel') {
      const csvPrefix = includeWatermark ? '"# [ KAYN\'IN ]"\n' : '';
      const content = `Titre: "${title}"\nDate: "${dateStr}"\n\n` + headers.join(",") + "\n" + rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
      const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvPrefix + content;
      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute("download", `Rapport_${activeDebate.id}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const watermarkCSS = includeWatermark 
        ? `body { position: relative; } body::before { content: "KAYN\\'IN"; position: fixed; top: 30%; left: 20%; font-size: 150px; color: #f1f5f9; transform: rotate(-45deg); z-index: -1; white-space: nowrap; font-family: sans-serif; font-weight: bold; }` 
        : ``;
      
      let html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head><meta charset='utf-8'><title>Rapport</title><style>\${watermarkCSS} table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; } th { padding: 10px; text-align: left; background: #e5e7eb; border: 1px solid #999; } td { padding: 8px; border: 1px solid #ccc; } h2, h3 { font-family: Arial, sans-serif; }</style></head>
        <body>
          <h2>Rapport de Débat</h2>
          <h3>Sujet : ${title}</h3>
          <p>Date de publication : ${dateStr}</p>`;
      
      if (activeDebate.adminConclusion) {
        html += `<h4>Conclusion de l'Administration</h4><p>${activeDebate.adminConclusion}</p>`;
      }
      
      html += `<h4>Registre des Participations</h4><table><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("")}</table>`;
      
      if (includeStats) {
         html += `<h4>Statistiques de Participation</h4>
                  <ul>
                    <li>Total des participations : ${realStats.total}</li>
                    <li>Répartition : ${realStats.hommes} Hommes / ${realStats.femmes} Femmes</li>
                    <li>Catégories : Citoyens (${realStats.citoyens}), ONG (${realStats.ong}), Partis (${realStats.partis}), Institutions (${realStats.institutions}).</li>
                  </ul>`;
         if (chartsImage) {
             html += `<br/><img src="${chartsImage}" style="max-width: 600px; width: 100%; height: auto;" /><br/>`;
         }
      }
      html += `</body></html>`;
      const blob = new Blob(['\ufeff', html], { type: 'application/msword' });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Rapport_${activeDebate.id}.doc`;
      link.click();
    }
  }
};
